import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ClipboardCheck } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate } from '@blimo/shared';
import { Badge, EmptyState, ErrorState, SearchInput } from '@blimo/ui';
import { CardsSkeleton } from '../../components/property-bits.jsx';
import { PageHeader } from '../../components/ui/page-header.jsx';
import { ItemList, ItemRow } from '../../components/ui/section.jsx';
import { DOCUMENT_SEARCH_MIN_LENGTH, SearchResults } from '../documents.jsx';

/**
 * Belegprüfung – checks visible to the user (decided by the API: auditors / owners).
 * Volltextsuche über die Belegprüfungs-Unterlagen (`GET /portal/documents/search`, erlaubt mit `document-checks.view`):
 * Kassenprüfer ohne `documents.view` haben keine Dokumente-Seite – die Sichtbarkeit der Treffer entscheidet der Server.
 */
export function DocumentChecksPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { data: checks, error, isPending, refetch } = useQuery(portalQueries.documentChecks(api));
  const [q, setQ] = useState('');
  const term = q.trim();
  const searching = term.length >= DOCUMENT_SEARCH_MIN_LENGTH;
  const results = useQuery(portalQueries.documentSearch(api, searching ? term : ''));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Belegprüfung" description="Unterlagen der Abrechnungsjahre Ihrer Liegenschaften." />
      {!error && !isPending && checks.length > 0 && (
        <SearchInput
          value={q}
          onValueChange={setQ}
          delay={300}
          label="Belege durchsuchen"
          placeholder="Titel und Inhalt der Unterlagen durchsuchen"
          wrapperClassName="sm:w-96"
        />
      )}
      {searching ? (
        // Treffer in Dokumente-Ordnern (nur mit documents.view) öffnen die Dokumente-Seite mit Hervorhebung
        <SearchResults results={results} term={term} onOpen={(doc) => navigate({ to: '/dokumente', search: { document: doc.id } })} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <CardsSkeleton />
      ) : checks.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="Keine Belegprüfungen"
          description="Als Kassenprüfer sehen Sie hier Prüfungen, sobald die Verwaltung sie freigibt. Als Eigentümer sehen Sie abgeschlossene Prüfungen der letzten fünf Jahre."
        />
      ) : (
        <ItemList aria-label="Belegprüfungen">
          {checks.map((check) => (
            <li key={check.id}>
              <ItemRow
                as={Link}
                to="/belegpruefung/$checkId"
                params={{ checkId: String(check.id) }}
                icon={ClipboardCheck}
                title={
                  <span>
                    Abrechnungsjahr <span className="numeric">{check.fiscal_year}</span> · {check.property?.name ?? `Liegenschaft #${check.property?.id}`}
                  </span>
                }
                subtitle={
                  <span className="flex flex-col gap-1.5">
                    {check.due_on && <span>Prüfung bis {formatDate(check.due_on)}</span>}
                    <CheckBadges check={check} />
                  </span>
                }
              />
            </li>
          ))}
        </ItemList>
      )}
    </div>
  );
}

/** Status badges – only API flags. @param {{ check: any }} props */
export function CheckBadges({ check }) {
  return (
    <span className="flex flex-wrap gap-1">
      {check.i_am_auditor && <Badge variant="secondary">Kassenprüfer</Badge>}
      {check.i_am_auditor && (check.confirmed_by_me ? <Badge variant="success">Von mir bestätigt</Badge> : <Badge variant="warning">Bestätigung offen</Badge>)}
      {check.is_ready && <Badge variant="info">Bereit</Badge>}
      {check.is_completed && <Badge variant="success">Abgeschlossen</Badge>}
    </span>
  );
}
